import React, { useState, useEffect, useMemo } from "react";
import { useForm, Controller } from "react-hook-form";
import { toast } from "react-toastify";
import { get, post, del } from "../../services/api";
import Select from "react-select";
import CustomDatatableGrid from "../../components/CustomDatatableGrid";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import "jspdf-autotable";

function AssignmentTab() {
  const token = localStorage.getItem("token");
  const organizationId = localStorage.getItem("organizationId");
  const userId = localStorage.getItem("userId");

  const [triggerRerender, setTriggerRerender] = useState(false);
  const [assignmentsData, setAssignmentsData] = useState([]);
  const [formTemplatesData, setFormTemplatesData] = useState([]);
  const [centersData, setCentersData] = useState([]);
  const [classesData, setClassesData] = useState([]);
  const [sectionsData, setSectionsData] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loading, setLoading] = useState(false);

  const {
    handleSubmit,
    control,
    setValue,
    watch,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues: {
      id: null,
      formTemplateId: null,
      centerId: null,
      classId: null,
      sectionId: null,
    },
  });

  const selectedCenter = watch("centerId");
  const selectedClass = watch("classId");

  // Fetch all assignments
  const getAssignments = async () => {
    try {
      setLoading(true);
      const response = await get("QAForm/QAAssignment", {
        headers: { Authorization: `Bearer ${token}` },
      });
      setAssignmentsData(response || []);
    } catch (error) {
      console.error("Error fetching assignments:", error);
      toast.error("Failed to load assignments");
    } finally {
      setLoading(false);
    }
  };

  // Fetch form templates
  const getFormTemplates = async () => {
    try {
      const response = await get("QAForm/templates", {
        headers: { Authorization: `Bearer ${token}` },
      });
      setFormTemplatesData(response || []);
    } catch (error) {
      console.error("Error fetching templates:", error);
    }
  };

  // Fetch centers
  const getCenters = async () => {
    try {
      const response = await get(
        `Dropdown/GetAllCentersByOrganizationId?organizationId=${organizationId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setCentersData(response || []);
    } catch (error) {
      console.error("Error fetching centers:", error);
    }
  };

  // Fetch classes when center changes
  useEffect(() => {
    const fetchClasses = async () => {
      if (selectedCenter) {
        try {
          const response = await get(
            `CenterClass/GetClassCenterByCenterId?id=${selectedCenter.value}`,
            { headers: { Authorization: `Bearer ${token}` } }
          );
          setClassesData(response || []);
        } catch (error) {
          console.error("Error fetching classes:", error);
        }
      } else {
        setClassesData([]);
        setValue("classId", null);
      }
    };
    fetchClasses();
  }, [selectedCenter, setValue]);

  // Fetch sections when class changes
  useEffect(() => {
    const fetchSections = async () => {
      if (selectedCenter && selectedClass) {
        try {
          const response = await get(
            `ClassSection/GetAllSectionByCenterClassIdAndCenterId?centerClassId=${selectedClass.value}&centerId=${selectedCenter.value}`,
            { headers: { Authorization: `Bearer ${token}` } }
          );
          setSectionsData(response || []);
        } catch (error) {
          console.error("Error fetching sections:", error);
        }
      } else {
        setSectionsData([]);
        setValue("sectionId", null);
      }
    };
    fetchSections();
  }, [selectedClass, selectedCenter, setValue]);

  useEffect(() => {
    getAssignments();
    getFormTemplates();
    getCenters();
  }, [triggerRerender]);

  const saveAssignment = async (data) => {
    setIsSubmitting(true);
    try {
      const apiData = {
        id: data.id || 0,
        formTemplateId: data.formTemplateId?.value,
        centerId: data.centerId?.value,
        classId: data.classId?.value || null,
        sectionId: data.sectionId?.value || null,
        userId: userId,
      };

      const endpoint = "QAForm/assign";
      const response = await post(endpoint, apiData, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.isSuccess) {
        toast.success(response.message || "Template assigned successfully!");
        setTriggerRerender((prev) => !prev);
        reset({
          id: null,
          formTemplateId: null,
          centerId: null,
          classId: null,
          sectionId: null,
        });
      } else {
        toast.error(response.message || "Failed to assign template");
      }
    } catch (error) {
      console.error("Error saving assignment:", error);
      toast.error("Failed to save assignment");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleToggleStatus = async (id) => {
    try {
      const response = await post(
        `QAForm/QAAssignment/${id}/toggle-status`,
        null,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      if (response.isSuccess) {
        toast.success(response.message);
        setTriggerRerender((prev) => !prev);
      }
    } catch (error) {
      toast.error("Failed to update status");
    }
  };
  const handleEdit = async (id) => {
    try {
      const response = await get(`QAForm/QAAssignmentById?id=${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      reset({
        id: response.id,
        formTemplateId: {
          value: response.formTemplateId,
          label: response.formTemplateName,
        },
        centerId: { value: response.centerId, label: response.centerName },
        classId: response.classId
          ? { value: response.classId, label: response.className }
          : null,
        sectionId: response.sectionId
          ? { value: response.sectionId, label: response.sectionName }
          : null,
        userId: response.userId,
      });
    } catch (error) {
      console.error("Error fetching assignment for edit:", error);
      toast.error("Failed to load assignment details");
    }
  };
  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this assignment?")) {
      try {
        const response = await get(`QAForm/DeleteQAAssignment?id=${id}`, null, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (response.isSuccess) {
          toast.success(response.message);
          setTriggerRerender((prev) => !prev);
        }
      } catch (error) {
        toast.error("Failed to delete assignment");
      }
    }
  };

  const columns = useMemo(
    () => [
      {
        accessorKey: "mrt-row-numbers",
        header: "Sr No.",
        size: 60,
        Cell: ({ row }) => row.index + 1,
      },
      {
        accessorKey: "formTemplateName",
        header: "Template Name",
        size: 200,
      },
      {
        accessorKey: "centerName",
        header: "Center",
        size: 150,
      },
      {
        accessorKey: "className",
        header: "Class",
        size: 150,
        Cell: ({ row }) =>
          row.original.className || (
            <span className="text-muted">Center Level</span>
          ),
      },
      {
        accessorKey: "sectionName",
        header: "Section",
        size: 150,
        Cell: ({ row }) =>
          row.original.sectionName ||
          (row.original.className ? (
            <span className="text-muted">Class Level</span>
          ) : (
            <span className="text-muted">-</span>
          )),
      },
      {
        accessorKey: "isActive",
        header: "Status",
        size: 100,
        Cell: ({ row }) => (
          <span
            className={`badge ${
              row.original.isActive ? "bg-success" : "bg-danger"
            }`}
          >
            {row.original.isActive ? "Active" : "Inactive"}
          </span>
        ),
      },
      {
        accessorKey: "actions",
        header: "Action",
        size: 120,
        Cell: ({ row }) => (
          <div className="d-flex gap-2">
            <button
              className="btn btn-sm p-0"
              onClick={() => handleToggleStatus(row.original.id)}
              title={row.original.isActive ? "Deactivate" : "Activate"}
            >
              <i
                className={`fas ${
                  row.original.isActive
                    ? "fa-ban text-warning"
                    : "fa-check text-success"
                }`}
              ></i>
            </button>
            <button
              className="btn btn-sm p-0"
              onClick={() => handleEdit(row.original.id)}
              title="Edit"
            >
              <i className={`fas fa-pencil`}></i>
            </button>
            <button
              className="btn btn-sm p-0"
              onClick={() => handleDelete(row.original.id)}
              title="Delete"
            >
              <i className="fas fa-trash text-danger"></i>
            </button>
          </div>
        ),
      },
    ],
    []
  );

  return (
    <div className="row g-3">
      <div className="col-md-4">
        <div className="card">
          <div className="card-header">
            <h5 className="card-title mb-0">Assign Form Template</h5>
          </div>
          <div className="card-body">
            <form onSubmit={handleSubmit(saveAssignment)} className="row g-3">
              <div className="col-12">
                <label className="form-label">
                  Center <span className="text-danger">*</span>
                </label>
                <Controller
                  name="centerId"
                  control={control}
                  rules={{ required: "Required" }}
                  render={({ field }) => (
                    <Select
                      {...field}
                      options={centersData.map((c) => ({
                        value: c.id,
                        label: c.name,
                      }))}
                      placeholder="Select Center"
                    />
                  )}
                />
                {errors.centerId && (
                  <small className="text-danger">
                    {errors.centerId.message}
                  </small>
                )}
              </div>
              <div className="col-12">
                <label className="form-label">
                  Form Template <span className="text-danger">*</span>
                </label>
                <Controller
                  name="formTemplateId"
                  control={control}
                  rules={{ required: "Required" }}
                  render={({ field }) => (
                    <Select
                      {...field}
                      options={formTemplatesData.map((t) => ({
                        value: t.id,
                        label: t.name,
                      }))}
                      placeholder="Select Template"
                    />
                  )}
                />
                {errors.formTemplateId && (
                  <small className="text-danger">
                    {errors.formTemplateId.message}
                  </small>
                )}
              </div>
              <div className="col-12">
                <label className="form-label">Class</label>
                <Controller
                  name="classId"
                  control={control}
                  render={({ field }) => (
                    <Select
                      {...field}
                      options={classesData.map((c) => ({
                        value: c.id,
                        label: c.name,
                      }))}
                      placeholder="Select Class (Optional)"
                      isClearable
                    />
                  )}
                />
              </div>

              <div className="col-12">
                <label className="form-label">Section</label>
                <Controller
                  name="sectionId"
                  control={control}
                  render={({ field }) => (
                    <Select
                      {...field}
                      options={sectionsData.map((s) => ({
                        value: s.id,
                        label: s.name,
                      }))}
                      placeholder="Select Section (Optional)"
                      isClearable
                    />
                  )}
                />
              </div>

              <div className="col-12 mt-4">
                <button
                  type="submit"
                  className="btn btn-primary btn-sm w-100"
                  disabled={isSubmitting}
                >
                  {isSubmitting
                    ? watch("id")
                      ? "Updating..."
                      : "Assigning..."
                    : watch("id")
                    ? "Update Template"
                    : "Assign Template"}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>

      <div className="col-md-8">
        <div className="card">
          <div className="card-header d-flex justify-content-between align-items-center">
            <h5 className="card-title mb-0">Assigned Templates</h5>
          </div>
          <div className="card-body">
            <CustomDatatableGrid
              columns={columns}
              data={assignmentsData}
              loading={loading}
              initialState={{
                pagination: { pageSize: 10, pageIndex: 0 },
              }}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

export default AssignmentTab;
